import { format } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

/**
 * Timezone identifiers for various regions
 * Ordered with US ET (Eastern Time) first, then UK, then Canadian timezones from east to west
 */
export const TIMEZONES = {
  US_EASTERN: 'America/New_York',
  UK: 'Europe/London',
  CANADA_ATLANTIC: 'America/Halifax',
  CANADA_NEWFOUNDLAND: 'America/St_Johns',
  CANADA_CENTRAL: 'America/Winnipeg',
  CANADA_MOUNTAIN: 'America/Edmonton',
  CANADA_PACIFIC: 'America/Vancouver',
} as const;

/**
 * Human-readable timezone labels
 * Ordered to match TIMEZONES order
 */
export const TIMEZONE_LABELS: Record<keyof typeof TIMEZONES, string> = {
  US_EASTERN: 'ET (US Eastern Time)',
  UK: 'UK/London',
  CANADA_ATLANTIC: 'Atlantic (Halifax)',
  CANADA_NEWFOUNDLAND: "Newfoundland (St. John's)",
  CANADA_CENTRAL: 'Central (Winnipeg)',
  CANADA_MOUNTAIN: 'Mountain (Calgary/Edmonton)',
  CANADA_PACIFIC: 'Pacific (Vancouver/Victoria)',
};

/**
 * Curated list of common timezones grouped by region
 * Used for the timezone selector dropdown
 */
export const TIMEZONE_GROUPS = {
  'North America': {
    'America/New_York': 'Eastern Time (New York)',
    'America/Chicago': 'Central Time (Chicago)',
    'America/Denver': 'Mountain Time (Denver)',
    'America/Los_Angeles': 'Pacific Time (Los Angeles)',
    'America/Anchorage': 'Alaska Time (Anchorage)',
    'America/Halifax': 'Atlantic Time (Halifax)',
    'America/St_Johns': "Newfoundland Time (St. John's)",
  },
  Europe: {
    'Europe/London': 'British Time (London)',
    'Europe/Paris': 'Central European Time (Paris)',
    'Europe/Berlin': 'Central European Time (Berlin)',
    'Europe/Madrid': 'Central European Time (Madrid)',
    'Europe/Rome': 'Central European Time (Rome)',
    'Europe/Amsterdam': 'Central European Time (Amsterdam)',
  },
  'Asia Pacific': {
    'Asia/Tokyo': 'Japan Time (Tokyo)',
    'Asia/Shanghai': 'China Time (Shanghai)',
    'Asia/Hong_Kong': 'Hong Kong Time',
    'Asia/Singapore': 'Singapore Time',
    'Asia/Dubai': 'Gulf Time (Dubai)',
    'Australia/Sydney': 'Eastern Australia (Sydney)',
    'Australia/Melbourne': 'Eastern Australia (Melbourne)',
    'Pacific/Auckland': 'New Zealand Time (Auckland)',
  },
  'South America': {
    'America/Sao_Paulo': 'Brasília Time (São Paulo)',
    'America/Argentina/Buenos_Aires': 'Argentina Time (Buenos Aires)',
    'America/Bogota': 'Colombia Time (Bogotá)',
  },
} as const;

/**
 * Format options for different date/time displays
 */
export const DATE_FORMAT = 'MMM d, yyyy';
export const TIME_FORMAT = 'h:mm a';
export const DATETIME_FORMAT = 'MMM d, yyyy h:mm a';
export const DATETIME_WITH_TIMEZONE_FORMAT = 'MMM d, yyyy h:mm a zzz';

/**
 * Convert a UTC date to various timezones
 * @param utcDate - The UTC date (Date object, ISO string, or timestamp)
 * @param formatString - Optional format string (defaults to DATETIME_WITH_TIMEZONE_FORMAT)
 * @returns Array of timezone conversions
 */
export function convertToTimezones(
  utcDate: Date | string | number,
  formatString: string = DATETIME_WITH_TIMEZONE_FORMAT
): Array<{ timezone: string; label: string; formatted: string }> {
  const date =
    typeof utcDate === 'string' || typeof utcDate === 'number' ? new Date(utcDate) : utcDate;

  return Object.entries(TIMEZONES).map(([key, tz]) => ({
    timezone: tz,
    label: TIMEZONE_LABELS[key as keyof typeof TIMEZONES],
    formatted: formatInTimeZone(date, tz, formatString),
  }));
}

/**
 * Format a UTC date in a specific timezone
 * @param utcDate - The UTC date (Date object, ISO string, or timestamp)
 * @param timezone - The IANA timezone identifier
 * @param formatString - The format string (defaults to DATETIME_WITH_TIMEZONE_FORMAT)
 * @returns Formatted date string
 */
export function formatInTimezone(
  utcDate: Date | string | number,
  timezone: string,
  formatString: string = DATETIME_WITH_TIMEZONE_FORMAT
): string {
  const date =
    typeof utcDate === 'string' || typeof utcDate === 'number' ? new Date(utcDate) : utcDate;

  return formatInTimeZone(date, timezone, formatString);
}

/**
 * Format a date as UTC with explicit UTC label
 * @param utcDate - The UTC date (Date object, ISO string, or timestamp)
 * @param formatString - The format string (defaults to DATETIME_FORMAT)
 * @returns Formatted UTC date string
 */
export function formatAsUTC(
  utcDate: Date | string | number,
  formatString: string = DATETIME_FORMAT
): string {
  const date =
    typeof utcDate === 'string' || typeof utcDate === 'number' ? new Date(utcDate) : utcDate;

  return format(date, formatString);
}

/**
 * Convert UTC date to user's timezone for display in form inputs
 * @param utcDate - UTC date string (ISO format) or Date object
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 * @param outputFormat - 'datetime-local' | 'date'
 * @returns Formatted string for input value
 */
export function convertUTCToTimezone(
  utcDate: string | Date,
  timezone: string,
  outputFormat: 'datetime-local' | 'date' = 'datetime-local'
): string {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  const formatString = outputFormat === 'datetime-local' ? "yyyy-MM-dd'T'HH:mm" : 'yyyy-MM-dd';

  return formatInTimeZone(date, timezone, formatString);
}

/**
 * Convert user's timezone input to UTC for API submission
 * @param localDateString - datetime-local input value (e.g., '2025-12-01T10:00')
 * @param timezone - IANA timezone the user is in
 * @returns ISO UTC string for API
 */
export function convertTimezoneToUTC(localDateString: string, timezone: string): string {
  // datetime-local format: '2025-12-01T10:00'
  // Treat as being in the user's timezone, convert to UTC
  const zonedDate = fromZonedTime(localDateString, timezone);
  return zonedDate.toISOString();
}

/**
 * Format a UTC date for display in user's timezone
 * @param utcDate - UTC date
 * @param timezone - IANA timezone
 * @param formatString - date-fns format string (default: DATETIME_FORMAT)
 * @returns Formatted date string in user's timezone
 */
export function formatInUserTimezone(
  utcDate: Date | string | number,
  timezone: string,
  formatString: string = DATETIME_FORMAT
): string {
  const date =
    typeof utcDate === 'string' || typeof utcDate === 'number' ? new Date(utcDate) : utcDate;

  return formatInTimeZone(date, timezone, formatString);
}

/**
 * Get browser's detected timezone
 * @returns IANA timezone string (e.g., 'America/New_York')
 */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York'; // Safe fallback
  }
}

/**
 * Get the UTC offset for a timezone at a specific date
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 * @param date - Optional date to check offset for (defaults to now)
 * @returns Offset string (e.g., 'UTC-5', 'UTC+9', 'UTC')
 */
export function getTimezoneOffset(timezone: string, date: Date = new Date()): string {
  const formatted = formatInTimeZone(date, timezone, 'xxx'); // Returns format like '-05:00' or '+09:00'
  const hours = formatted.slice(0, 3); // '-05' or '+09'
  const minutes = formatted.slice(4); // '00' or '30'

  if (hours === '+00' && minutes === '00') {
    return 'UTC';
  }

  // Remove leading '+' and format nicely
  const hourNum = parseInt(hours, 10);
  if (minutes === '00') {
    return `UTC${hourNum > 0 ? '+' : ''}${hourNum}`;
  }
  return `UTC${hourNum > 0 ? '+' : ''}${hourNum}:${minutes}`;
}

/**
 * Get current time formatted for a specific timezone
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 * @returns Formatted current time string
 */
export function getCurrentTimeInTimezone(timezone: string): string {
  return formatInTimeZone(new Date(), timezone, 'h:mm:ss a');
}

/**
 * Get the timezone abbreviation (e.g., "EST", "PST", "JST")
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 * @param date - Optional date to check abbreviation for (defaults to now)
 * @returns Timezone abbreviation string (e.g., "EST")
 */
export function getTimezoneAbbreviation(timezone: string, date: Date = new Date()): string {
  try {
    const formatted = formatInTimeZone(date, timezone, 'zzz'); // Returns abbreviation like "EST", "PST", "JST"
    return formatted;
  } catch {
    return 'Local'; // Fallback if timezone is invalid
  }
}
